import clsx from 'clsx';
import { truncateStringInMiddle } from '../../../../../../_metronic/helpers';
import CopyTextButton from '../../../../../modules/components/copyText/CopyTextButton';
import React from 'react';
import { useIntl } from 'react-intl';
import InfoTooltip from '../../../../../modules/components/tooltip/InfoTooltip';

export type UrlWithMetadataFieldType = 'url' | 'title' | 'description' | 'linkedSites';

const highlightStats = [
  'likes',
  'followers',
  'following',
  'posts',
  'views',
  'comments',
  'shares',
  'subscribers',
  'members',
];

const parseStats = (
  stats: string[],
  text: string,
  lookAfter: boolean = false
): { label: string; value: string }[] => {
  if (!(stats?.length > 0) || !text) return [];
  const numRegex = /\d+(?:[.,\s]\d+)*[KkMmBb]?\+?/;

  return stats
    .map((label) => {
      let regex: RegExp;

      if (lookAfter) {
        regex = new RegExp(`${label}[:\\s]*(${numRegex.source})`, 'i');
      } else {
        regex = new RegExp(`(${numRegex.source})\\s*${label}`, 'i');
      }

      const match = text.match(regex);

      if (!match) return null;

      const numberPart = match[1].replace(/[\s,]/g, '');

      const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();

      return {
        label: capitalizedLabel,
        value: numberPart,
      };
    })
    .filter((result): result is { label: string; value: string } => result !== null);
};

interface Props {
  url: string;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  linkedSites?: string[];
  isSiAiNewData?: boolean;
  truncateAll?: boolean;
  isStatsHighlight?: boolean;
  className?: {
    container?: string;
  };

  isEditing?: boolean;
  onUpdate?: (field: UrlWithMetadataFieldType, value: string | string[]) => void;
  onRemove?: () => void;
}

export default function UrlWithMetadataCard({
  url,
  icon,
  title,
  description,
  linkedSites,
  isSiAiNewData,
  truncateAll,
  isStatsHighlight,
  className,
  isEditing,
  onUpdate,
  onRemove,
}: Props) {
  const intl = useIntl();
  const urlContainerMaxWidth = {
    normal: 1500, // px
    print: 700,
  };
  const linkedUrlContainerMaxWidth = 300; // px
  const inputFieldClassName = 'form-control form-control-sm fw-normal text-dark';

  const [links, setLinks] = React.useState<string>(linkedSites?.join(',') || '');

  return (
    <div
      className={clsx(
        'vstack border rounded p-4 gap-1 position-relative',
        className?.container,
        isEditing && 'bg-light'
      )}
    >
      {isEditing && (
        <div className='position-absolute top-0 start-100' style={{ translate: '-40% -30%' }}>
          <i
            className={clsx('bi bi-dash-square-fill fs-1 text-danger cursor-pointer')}
            title='Remove'
            onClick={onRemove}
          ></i>
        </div>
      )}

      {/* Title */}
      {isEditing ? (
        <div className='form-floating'>
          <input
            type='text'
            className={inputFieldClassName}
            value={title}
            onChange={(e) => onUpdate('title', e.target.value)}
            placeholder='Title' // required for floating labels
          />
          <label>Title</label>
        </div>
      ) : (
        title && (
          <span className={clsx('fs-6', truncateAll && 'text-truncate')}>
            {icon}
            <span className='align-middle' title={truncateAll && title}>
              {title}
            </span>
          </span>
        )
      )}

      {/* URL */}
      {isEditing ? (
        <div className='form-floating'>
          <input
            type='text'
            className={inputFieldClassName}
            value={url}
            onChange={(e) => onUpdate('url', e.target.value)}
            placeholder='https://other-domain.com' // required for floating labels
          />
          <label className='required'>
            Link <small>(must include http:// or https://)</small>
          </label>
        </div>
      ) : (
        <span>
          {!title && icon}
          <a
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            title={url}
            className='fs-7 text-break align-middle'
            style={{ width: 'fit-content' }}
          >
            {truncateAll ? (
              truncateStringInMiddle(url, urlContainerMaxWidth.print)
            ) : (
              <>
                <span className='d-print-block d-none'>
                  {truncateStringInMiddle(url, urlContainerMaxWidth.print)}
                </span>
                <span className='d-print-none'>
                  {truncateStringInMiddle(url, urlContainerMaxWidth.normal)}
                </span>
              </>
            )}
          </a>
          <CopyTextButton text={url} />
        </span>
      )}

      {/* Description */}
      {isEditing ? (
        <div className='form-floating'>
          <textarea
            className={inputFieldClassName}
            value={description}
            onChange={(e) => onUpdate('description', e.target.value)}
            placeholder='Description' // required for floating labels
          />
          <label>Description</label>
        </div>
      ) : (
        <span
          className={clsx('fs-7 text-muted', truncateAll && 'text-truncate')}
          title={truncateAll && description}
        >
          {description}
        </span>
      )}
      {isStatsHighlight && (
        <span className='fs-7'>
          {parseStats(highlightStats, description).map((stat, index) => (
            <span key={index} className='mark align-middle me-1'>
              <span className='me-1'>{stat.label}:</span>
              <span className='fw-bold'>{stat.value}</span>
            </span>
          ))}
        </span>
      )}

      {/* Linked Sites */}
      {!isSiAiNewData && (
        <div
          className={clsx(
            'd-flex flex-wrap align-items-center',
            isEditing ? 'gap-1 mt-1' : 'gap-2'
          )}
        >
          <span className={clsx('fs-7', isEditing && 'required')}>
            {intl.formatMessage({ id: 'COMMON.LINKED_SITES' })}:
            {isEditing && (
              <InfoTooltip
                id='linked_sites_tooltip'
                placement='top'
                iconFontSize='fs-4'
                className='ps-2 align-middle'
              >
                <p className='m-0'>
                  Separate URLs with a comma <code>,</code>. Include http:// or https://
                </p>
              </InfoTooltip>
            )}
          </span>
          {isEditing ? (
            <textarea
              className={inputFieldClassName}
              value={links}
              onChange={(e) => setLinks(e.target.value.trim())}
              onBlur={(e) => {
                const val = e.target.value.trim().replace(/,+/g, ',').replace(/^,|,$/g, '');
                if (val !== linkedSites.join(',')) {
                  onUpdate('linkedSites', val.split(','));
                }
                setLinks(val);
              }}
              placeholder='https://example.com,https://example.com/products' // required for floating labels
            />
          ) : (
            linkedSites.map((site, idx) => (
              <span
                key={idx}
                className='badge badge-sm badge-light p-2 px-3 rounded-pill fs-8'
                title={site}
              >
                <a
                  href={site}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-gray-600 text-hover-primary'
                >
                  {truncateStringInMiddle(
                    site.replace(/^https?:\/\//, ''),
                    linkedUrlContainerMaxWidth
                  )}
                </a>
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
}
